// Date and label formatting shared by the redesigned screens (en-US, local time).

const opt = (o: Intl.DateTimeFormatOptions) => (d: Date | string) =>
  (typeof d === 'string' ? new Date(d) : d).toLocaleDateString('en-US', o);

/** "Sun, Nov 8" */
export const shortDate = opt({ weekday: 'short', month: 'short', day: 'numeric' });
/** "Sunday, November 8" */
export const longDate = opt({ weekday: 'long', month: 'long', day: 'numeric' });
/** "Nov 8" */
export const monthDay = opt({ month: 'short', day: 'numeric' });
/** "Nov 8, 2026" */
export const monthDayYear = opt({ month: 'short', day: 'numeric', year: 'numeric' });
/** "Sun" */
export const weekdayShort = opt({ weekday: 'short' });
/** "Nov" */
export const monthShort = opt({ month: 'short' });

/** "9:00 AM" */
export const time12 = (d: Date | string): string => {
  const x = typeof d === 'string' ? new Date(d) : d;
  const h = x.getHours();
  return `${h % 12 || 12}:${String(x.getMinutes()).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

/** A service is past once its start time has gone by. */
export const isPastDate = (d: Date | string, now = new Date()): boolean =>
  (typeof d === 'string' ? new Date(d) : d).getTime() < now.getTime();

/** "12 min ago", "2 hours ago", "Yesterday", "Thursday", "Oct 3" */
export const relativeWhen = (d: Date | string, now = new Date()): string => {
  const x = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(x.getTime())) return '';
  const mins = Math.round((now.getTime() - x.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (x >= startOfToday) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor((startOfToday.getTime() - new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()) / 86400000) + 1;
  if (days === 1) return 'Yesterday';
  if (days < 7) return x.toLocaleDateString('en-US', { weekday: 'long' });
  return monthDay(x);
};

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
